import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Calendar, CheckCircle2, Inbox, Info, Mail, MessageSquare, Trophy, XCircle } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  rectIntersection,
} from '@dnd-kit/core';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { AdminPage, KpiCard, StatusBadge, formatDate, formatNumber } from '../../../components/design-system/admin';
import { useQuoteAdminApi } from '../../../common/hooks/use-quote-admin-api.hook';
import { QuoteRequest, QuoteStatus } from '../../../common/models/billing.model';
import {
  FEATURE_KEYS,
  KanbanColumn,
  QuoteCardOverlay,
  QuotePrice,
  STATUS_META,
  STATUS_ORDER,
  VOLUME_KEYS,
  annualQuoteValue,
  usePriceText,
} from '../../../modules/superadmin/billing/quote-board';
import { formatTotals } from '../../../modules/superadmin/billing/money';

// A section heading inside the detail sheet (a Label when it names a field).
const SECTION_LABEL = 'text-xs font-medium text-muted-foreground';

/**
 * Super-admin Enterprise quote-request pipeline, as a Kanban board: one column
 * per status (new → contacted → won / lost). Drag a card to another column to
 * change its status (optimistic + persisted). Click a card to open its full
 * details in a right-hand sheet, where status, price, fee and an internal note
 * can be edited.
 */
export const QuoteRequestsPage: React.FC = () => {
  const { t } = useTranslation();
  const api = useQuoteAdminApi();
  const priceText = usePriceText();

  const [quotes, setQuotes] = useState<QuoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selected, setSelected] = useState<QuoteRequest | null>(null);
  const [editStatus, setEditStatus] = useState<QuoteStatus>('new');
  const [editNote, setEditNote] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editCurrency, setEditCurrency] = useState('USD');
  const [editInterval, setEditInterval] = useState<'month' | 'year'>('year');
  // Blank means "inherit the plan's integration fee"; an explicit 0 waives it.
  const [editFee, setEditFee] = useState('');
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setQuotes((await api.listQuoteRequests()) || []);
    } catch {
      toast.error(t('ADMIN_SALES.TOAST.LOAD_FAILED'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g: Record<QuoteStatus, QuoteRequest[]> = { new: [], contacted: [], won: [], lost: [] };
    for (const q of quotes) (g[q.status] || g.new).push(q);
    return g;
  }, [quotes]);

  // What the won deals are worth a year, per currency (monthly quotes × 12).
  const wonValue = useMemo(() => {
    const totals = annualQuoteValue(grouped.won);
    return totals.size ? formatTotals(totals) : null;
  }, [grouped]);

  const activeQuote = useMemo(() => quotes.find((q) => q._id === activeId) || null, [quotes, activeId]);

  const handleDragEnd = async (event: any) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const newStatus = over.id as QuoteStatus;
    const quote = quotes.find((q) => q._id === active.id);
    if (!quote || !STATUS_ORDER.includes(newStatus) || quote.status === newStatus) return;

    const prev = quote.status;
    setQuotes((qs) => qs.map((q) => (q._id === quote._id ? { ...q, status: newStatus } : q)));
    try {
      await api.updateQuote(quote._id, { status: newStatus });
      toast.success(t('ADMIN_SALES.TOAST.MOVED', { status: t(STATUS_META[newStatus].label) }));
    } catch {
      setQuotes((qs) => qs.map((q) => (q._id === quote._id ? { ...q, status: prev } : q))); // revert
      toast.error(t('ADMIN_SALES.TOAST.MOVE_FAILED'));
    }
  };

  const openDetail = (q: QuoteRequest) => {
    setSelected(q);
    setEditStatus(q.status);
    setEditNote(q.adminNote || '');
    setEditAmount(q.quotedAmount !== undefined && q.quotedAmount !== null ? String(q.quotedAmount) : '');
    setEditCurrency(q.quotedCurrency || 'USD');
    setEditInterval(q.quotedInterval || 'year');
    setEditFee(q.integrationFee !== undefined && q.integrationFee !== null ? String(q.integrationFee) : '');
  };

  const saveDetail = async () => {
    if (!selected) return;
    setSaving(true);
    const hasPrice = editAmount !== '' && !Number.isNaN(Number(editAmount));
    // Blank is left out of the patch entirely so it keeps inheriting the plan's
    // fee; a typed 0 is sent, which waives it for this deal.
    const hasFee = editFee !== '' && !Number.isNaN(Number(editFee));
    const patch = {
      status: editStatus,
      note: editNote.trim() || undefined,
      ...(hasPrice ? { quotedAmount: Number(editAmount), quotedCurrency: editCurrency.trim() || 'USD', quotedInterval: editInterval } : {}),
      ...(hasFee ? { integrationFee: Number(editFee) } : {}),
    };
    try {
      await api.updateQuote(selected._id, patch);
      const applied = {
        status: editStatus,
        adminNote: editNote.trim() || null,
        ...(hasPrice ? { quotedAmount: Number(editAmount), quotedCurrency: editCurrency.trim() || 'USD', quotedInterval: editInterval } : {}),
        ...(hasFee ? { integrationFee: Number(editFee) } : {}),
      };
      setQuotes((qs) => qs.map((q) => (q._id === selected._id ? { ...q, ...applied } : q)));
      setSelected((s) => (s ? { ...s, ...applied } : s));
      toast.success(t('ADMIN_SALES.TOAST.UPDATED'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('ADMIN_SALES.TOAST.UPDATE_FAILED'));
    } finally {
      setSaving(false);
    }
  };

  const setupBilling = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await api.setupQuoteBilling(selected._id);
      setQuotes((qs) => qs.map((q) => (q._id === selected._id ? { ...q, ...updated } : q)));
      setSelected((s) => (s ? { ...s, ...updated } : s));
      setEditStatus(updated.status);
      toast.success(t('ADMIN_SALES.TOAST.BILLING_SET_UP'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('ADMIN_SALES.TOAST.SETUP_FAILED'));
    } finally {
      setSaving(false);
    }
  };

  const selVolumes = Object.entries(selected?.volumes || {});
  const firstLoad = loading && quotes.length === 0;
  const editHasPrice = editAmount !== '' && !Number.isNaN(Number(editAmount));

  return (
    <AdminPage
      title={t('ADMIN_SHELL.NAV.QUOTES')}
      parents={[{ label: t('ADMIN_SHELL.NAV.SECTION_SALES') }]}
      description={t('ADMIN_SALES.QUOTES.DESCRIPTION')}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="quote-kpis">
        <KpiCard
          label={t('ADMIN_SALES.STATUS.NEW')}
          value={formatNumber(grouped.new.length)}
          hint={t('ADMIN_SALES.KPI.NEW_HINT')}
          icon={<Inbox />}
          loading={firstLoad}
          testId="kpi-quotes-new"
        />
        <KpiCard
          label={t('ADMIN_SALES.STATUS.CONTACTED')}
          value={formatNumber(grouped.contacted.length)}
          hint={t('ADMIN_SALES.KPI.CONTACTED_HINT')}
          icon={<MessageSquare />}
          loading={firstLoad}
          testId="kpi-quotes-contacted"
        />
        <KpiCard
          label={t('ADMIN_SALES.STATUS.WON')}
          value={formatNumber(grouped.won.length)}
          hint={wonValue ? t('ADMIN_SALES.KPI.WON_VALUE', { value: wonValue }) : t('ADMIN_SALES.KPI.WON_HINT')}
          icon={<Trophy />}
          loading={firstLoad}
          testId="kpi-quotes-won"
        />
        <KpiCard
          label={t('ADMIN_SALES.STATUS.LOST')}
          value={formatNumber(grouped.lost.length)}
          hint={t('ADMIN_SALES.KPI.LOST_HINT')}
          icon={<XCircle />}
          loading={firstLoad}
          testId="kpi-quotes-lost"
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STATUS_ORDER.map((s) => <Skeleton key={s} className="h-80 rounded-xl" />)}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={rectIntersection}
          onDragStart={(e) => setActiveId(String(e.active.id))}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STATUS_ORDER.map((status) => (
              <KanbanColumn key={status} status={status} quotes={grouped[status]} onOpen={openDetail} />
            ))}
          </div>
          <DragOverlay>
            {activeQuote ? <QuoteCardOverlay quote={activeQuote} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent side="right" className="w-full gap-0 p-0 text-sm sm:max-w-[440px]">
          {selected && (
            <>
              <SheetHeader className="gap-1 border-b p-6 pe-12">
                <div className="flex items-start justify-between gap-3">
                  <SheetTitle className="min-w-0 font-heading text-xl font-medium tracking-tight">
                    {selected.vendorName}
                  </SheetTitle>
                  <StatusBadge tone={STATUS_META[selected.status]?.tone || 'neutral'} className="mt-1.5">
                    {t(STATUS_META[selected.status]?.label || STATUS_META.new.label)}
                  </StatusBadge>
                </div>
                <SheetDescription className="text-sm">
                  {selected.planName ? t('ADMIN_SALES.DETAIL.PLAN', { plan: selected.planName }) : t('ADMIN_SALES.DETAIL.NO_PLAN')}
                </SheetDescription>
                <div className="mt-3 flex flex-col gap-1.5 text-muted-foreground">
                  {selected.vendorEmail && (
                    <div className="flex items-center gap-2">
                      <Mail className="size-4 shrink-0" />
                      <a href={`mailto:${selected.vendorEmail}`} className="truncate hover:underline">{selected.vendorEmail}</a>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar className="size-4 shrink-0" />
                    <span>
                      {t('ADMIN_SALES.DETAIL.REQUESTED', { date: formatDate(selected.createdAt) })}
                      {selected.handledBy ? ` · ${t('ADMIN_SALES.DETAIL.HANDLED_BY', { name: selected.handledBy })}` : ''}
                    </span>
                  </div>
                </div>
              </SheetHeader>

              <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
                {/* Editable status */}
                <div className="grid gap-2">
                  <Label htmlFor="quote-status" className={SECTION_LABEL}>{t('ADMIN_SALES.DETAIL.STATUS')}</Label>
                  <Select value={editStatus} onValueChange={(v) => setEditStatus(v as QuoteStatus)}>
                    <SelectTrigger id="quote-status" className="w-full" aria-label={t('ADMIN_SALES.DETAIL.STATUS')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_ORDER.map((st) => (
                        <SelectItem key={st} value={st}>
                          {t(STATUS_META[st].label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Quoted price */}
                <div className="grid gap-2">
                  <Label htmlFor="quote-amount" className={SECTION_LABEL}>{t('ADMIN_SALES.DETAIL.QUOTED_PRICE')}</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      id="quote-amount"
                      type="number"
                      min={0}
                      placeholder={t('ADMIN_SALES.DETAIL.AMOUNT_PLACEHOLDER')}
                      className="min-w-[120px] flex-1 tabular-nums"
                      value={editAmount}
                      onChange={(e) => setEditAmount(e.target.value)}
                      aria-label={t('ADMIN_SALES.DETAIL.AMOUNT')}
                    />
                    <Input
                      placeholder="USD"
                      maxLength={6}
                      className="w-[84px]"
                      value={editCurrency}
                      onChange={(e) => setEditCurrency(e.target.value.toUpperCase())}
                      aria-label={t('ADMIN_SALES.DETAIL.CURRENCY')}
                    />
                    <ToggleGroup
                      type="single"
                      variant="outline"
                      size="sm"
                      value={editInterval}
                      onValueChange={(v) => v && setEditInterval(v as 'month' | 'year')}
                      aria-label={t('ADMIN_SALES.DETAIL.INTERVAL')}
                    >
                      <ToggleGroupItem value="month" aria-label={t('ADMIN_SALES.PRICE.PER_MONTH_LABEL')}>
                        {t('ADMIN_SALES.PRICE.MONTH_SHORT')}
                      </ToggleGroupItem>
                      <ToggleGroupItem value="year" aria-label={t('ADMIN_SALES.PRICE.PER_YEAR_LABEL')}>
                        {t('ADMIN_SALES.PRICE.YEAR_SHORT')}
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                  {editHasPrice && (
                    <p className="flex flex-wrap items-baseline gap-1.5 text-xs text-muted-foreground" data-testid="quote-price-preview">
                      <span>{t('ADMIN_SALES.DETAIL.PREVIEW')}</span>
                      <QuotePrice amount={Number(editAmount)} currency={editCurrency} interval={editInterval} className="text-sm text-foreground" />
                    </p>
                  )}
                </div>

                {/* One-time integration fee — billed on the first invoice only */}
                <div className="grid gap-2">
                  <Label htmlFor="quote-integration-fee" className={SECTION_LABEL}>{t('ADMIN_SALES.DETAIL.INTEGRATION_FEE')}</Label>
                  <Input
                    id="quote-integration-fee"
                    type="number"
                    min={0}
                    placeholder={t('ADMIN_SALES.DETAIL.INTEGRATION_FEE_PLACEHOLDER')}
                    className="w-full tabular-nums"
                    value={editFee}
                    onChange={(e) => setEditFee(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{t('ADMIN_SALES.DETAIL.INTEGRATION_FEE_HELP')}</p>
                </div>

                {/* Payment method (chosen by the vendor in the configurator) and billing */}
                <div className="grid gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className={SECTION_LABEL}>{t('ADMIN_SALES.DETAIL.PAYMENT')}</span>
                    <span className="font-medium">
                      {selected.paymentMethod === 'wire' ? t('ADMIN_SALES.DETAIL.PAYMENT_WIRE') : t('ADMIN_SALES.DETAIL.PAYMENT_CARD')}
                    </span>
                  </div>

                  {/* Accept / set up billing — Stripe only; wire follows the invoice workflow */}
                  {selected.paymentMethod === 'wire' ? (
                    <div className="flex items-start gap-2 rounded-lg bg-muted p-3">
                      <Info className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                      <p className="text-muted-foreground">{t('ADMIN_SALES.DETAIL.WIRE_NOTE')}</p>
                    </div>
                  ) : selected.stripePriceId ? (
                    <div className="flex items-start gap-2 rounded-lg bg-muted p-3">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
                      <p>
                        {t('ADMIN_SALES.DETAIL.BILLING_ENABLED', {
                          price: priceText(selected.quotedAmount, selected.quotedCurrency, selected.quotedInterval) || '—',
                        })}
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-1.5">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={setupBilling}
                        disabled={saving || selected.quotedAmount === undefined || selected.quotedAmount === null}
                      >
                        {saving ? t('ADMIN_SALES.DETAIL.WORKING') : t('ADMIN_SALES.DETAIL.SETUP_BILLING')}
                      </Button>
                      <p className="text-xs text-muted-foreground">{t('ADMIN_SALES.DETAIL.SETUP_BILLING_HELP')}</p>
                    </div>
                  )}
                </div>

                {/* Features */}
                <div className="grid gap-2">
                  <span className={SECTION_LABEL}>{t('ADMIN_SALES.DETAIL.FEATURES')}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {(selected.selectedFeatures || []).length === 0 ? (
                      <p className="text-muted-foreground">{t('ADMIN_SALES.DETAIL.NO_FEATURES')}</p>
                    ) : (
                      selected.selectedFeatures.map((k) => (
                        <Badge key={k} variant="secondary">
                          {FEATURE_KEYS[k] ? t(`ADMIN_SALES.FEATURES.${FEATURE_KEYS[k]}`) : k}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>

                {/* Volumes */}
                {selVolumes.length > 0 && (
                  <div className="grid gap-1">
                    <span className={`${SECTION_LABEL} mb-1`}>{t('ADMIN_SALES.DETAIL.VOLUMES')}</span>
                    {selVolumes.map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-3 py-1">
                        <span className="text-muted-foreground">{VOLUME_KEYS[k] ? t(`ADMIN_SALES.VOLUMES.${VOLUME_KEYS[k]}`) : k}</span>
                        <span className="font-medium tabular-nums">
                          {(v as number) === -1 ? t('ADMIN_SHELL.COMMON.UNLIMITED') : formatNumber(v as number)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Visualizer quality */}
                {selected.visualizationQuality && (
                  <div className="flex items-center justify-between gap-3">
                    <span className={SECTION_LABEL}>{t('ADMIN_SALES.DETAIL.QUALITY')}</span>
                    <span className="font-medium">
                      {t(`ADMIN_SALES.QUALITY.${String(selected.visualizationQuality).toUpperCase()}`)}
                    </span>
                  </div>
                )}

                {/* Message */}
                {selected.message && (
                  <div className="grid gap-2">
                    <span className={SECTION_LABEL}>{t('ADMIN_SALES.DETAIL.MESSAGE')}</span>
                    <p className="rounded-lg bg-muted p-3 whitespace-pre-wrap">{selected.message}</p>
                  </div>
                )}

                {/* Internal note */}
                <div className="grid gap-2">
                  <Label htmlFor="quote-note" className={SECTION_LABEL}>{t('ADMIN_SALES.DETAIL.NOTE')}</Label>
                  <Textarea
                    id="quote-note"
                    rows={3}
                    placeholder={t('ADMIN_SALES.DETAIL.NOTE_PLACEHOLDER')}
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                  />
                </div>
              </div>

              <SheetFooter className="mt-0 border-t p-4">
                <Button className="w-full" onClick={saveDetail} disabled={saving}>
                  {saving ? t('ADMIN_SALES.DETAIL.SAVING') : t('ADMIN_SALES.DETAIL.SAVE')}
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AdminPage>
  );
};

export default QuoteRequestsPage;
